import { action, makeObservable, observable, runInAction } from 'mobx';
import {
  AuthToken,
  AuthTokenService,
  DataResponse,
  initResponseInterceptor,
  initBearerToken,
  User,
  UserService,
  UserGroupService,
  UserGroup,
} from 'us-web-services';
import { Storage } from '../../constants';
import { Routes } from '../../Routes';

export class UserStore {
  user: User;
  temporaryPhoto: string;
  token: AuthToken;
  #activeTests: UserGroup[];
  #refreshRequest?: Promise<DataResponse<AuthToken>>;

  constructor() {
    const localUser = localStorage.getItem(Storage.US_USER_OBJECT);
    const token = localStorage.getItem(Storage.US_USER_AUTH_TOKEN);
    const refreshToken = localStorage.getItem(Storage.US_USER_REFRESH_TOKEN);

    this.temporaryPhoto = '';
    this.#refreshRequest = undefined;
    this.#activeTests = localStorage.getItem(Storage.US_USER_TESTS_OBJECT) ? JSON.parse(localStorage.getItem(Storage.US_USER_TESTS_OBJECT) || '') : [];
    if (!Array.isArray(this.#activeTests)) {
      this.#activeTests = [];
    }

    this.user = localUser ? JSON.parse(localUser) as User : {};

    this.token = {
      userId: this.user.id,
      token: token ?? null,
      refreshToken: { token: refreshToken ?? null },
    };

    const authentication = {
      user: {
        id: this.user.id ?? 0,
      },
      updateToken: this.updateToken,
    };

    initBearerToken();
    initResponseInterceptor(authentication);

    if (this.user.id) {
      this.fetchUserGroups();
    }

    makeObservable(this, {
      logout: action,
      refreshAuthToken: action,
      refreshUser: action,
      setTemporaryPhoto: action,
      updateUser: action,
      temporaryPhoto: observable,
      token: observable,
      user: observable,
    });
  }

  async refreshUser(): Promise<void> {
    if (this.user.id) {
      try {
        const response = (await UserService.get(this.user.id)).data;

        // needs a runInAction after an async call
        runInAction(() => {
          this.updateUser(response.data);
        });
      } catch (error) {
        console.warn('Refreshing user failed', error);
      }
    }
  }

  setTemporaryPhoto(photo: string): void {
    this.temporaryPhoto = photo;
  }

  updateUser(user: User): void {
    if (user.id) {
      this.user = user;
      localStorage.setItem(Storage.US_USER_OBJECT, JSON.stringify(this.user));
    } else {
      this.user = {};
      localStorage.removeItem(Storage.ORIGINAL_USER_ID);
      localStorage.removeItem(Storage.US_USER_OBJECT);
      localStorage.removeItem(Storage.US_PARENT_OBJECT);
      localStorage.removeItem(Storage.US_SITTER_OBJECT);
    }
  }

  updateToken(token: AuthToken): void {
    this.token = token;

    if (this.token?.token) {
      localStorage.setItem(Storage.US_USER_AUTH_TOKEN, this.token.token);
    } else {
      localStorage.removeItem(Storage.US_USER_AUTH_TOKEN);
    }

    if (this.token?.refreshToken?.token) {
      localStorage.setItem(Storage.US_USER_REFRESH_TOKEN, this.token.refreshToken.token);
    } else {
      localStorage.removeItem(Storage.US_USER_REFRESH_TOKEN);
    }
  }

  logout(): void {
    this.updateUser({});
    this.updateToken({});
    localStorage.removeItem(Storage.US_USER_TESTS_OBJECT);

    // Defer redirect to next event loop tick to ensure localStorage operations complete
    setTimeout(() => {
      window.location.href = Routes.login;
    }, 0);
  }

  async refreshAuthToken(): Promise<void> {
    if (this.user.id) {
      const authToken: AuthToken = {
        userId: this.user.id,
        refreshToken: this.token?.refreshToken,
      };
      const config = { headers: { Accept: 'application/json' } };

      if (!this.#refreshRequest) {
        this.#refreshRequest = AuthTokenService.create(authToken, config);
      }

      try {
        const response = (await this.#refreshRequest).data;
        this.updateToken(response.data);
      } catch {
        this.logout();
      }
    }
  }

  #setActiveTests(activeTests: UserGroup[], setLocalStorage = true): void {
    if (activeTests.length > 0) {
      this.#activeTests = activeTests;
      if (setLocalStorage) {
        localStorage.setItem(Storage.US_USER_TESTS_OBJECT, JSON.stringify(this.#activeTests));
      }
    }
  }

  async fetchUserGroups(): Promise<void> {
    const config = {
      params: {
        userId: this.user.id
      },
    };

    try {
      const response = (await UserGroupService.getByFilter(config)).data;
      this.#setActiveTests(response.data);
    } catch {
      this.#setActiveTests([]);
    }
  }

  inTestGroup(groupName: string): boolean {
    return this.#activeTests ? this.#activeTests.some(userGroup => userGroup.group?.canonicalName === groupName) : false;
  }

  // sample computed
  // get userFullName() {
  //   return this.user.firstName + this.user.lastName;
  // }
}
